import React from "react";
import { Container, Overline } from "@/components/primitives";
import { MarkOpening, MarkBeyond, MarkOffset, Wordmark } from "@/components/BrandLogos";
import { useDocumentMeta } from "@/lib/meta";

const routes = [
  {
    id: "1",
    name: "Route 1 — The Opening",
    idea: "A precise frame with a break in the top edge; the removed segment sits offset above — the piece that has moved beyond the box.",
    Mark: MarkOpening,
  },
  {
    id: "2",
    name: "Route 2 — Beyond the Line",
    idea: "A closed frame with one edge overshooting the boundary. A single, controlled line extending past the box.",
    Mark: MarkBeyond,
  },
  {
    id: "3",
    name: "Route 3 — The Offset",
    idea: "Two corners of a frame, displaced. The inner boundary has escaped the outer one — architectural and severe.",
    Mark: MarkOffset,
  },
];

function Swatch({ dark, children, label }) {
  return (
    <div className="border border-border">
      <div className="border-b border-border px-4 py-2">
        <span className="overline text-muted-foreground">{label}</span>
      </div>
      <div
        className={`flex items-center justify-center gap-8 px-6 py-10 ${
          dark ? "bg-[#0C0C0C] text-[#EDEDED]" : "bg-[#FBFBF9] text-[#111111]"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function Brand() {
  useDocumentMeta({ title: "Brand — Logo routes", description: "Beyond the Box logo routes.", path: "/brand" });
  return (
    <div data-testid="brand-page">
      <section className="border-b border-border">
        <Container className="py-16 md:py-20">
          <Overline accent>Brand · Logo routes</Overline>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
            Beyond the Box — three monochrome routes.
          </h1>
          <p className="mt-6 max-w-2xl leading-relaxed text-muted-foreground">
            Every route is built as SVG from a single stroke weight, uses one restrained oxblood
            accent, and is legible from favicon (16px) to masthead. Concept: a controlled frame with
            a precise element moving beyond it — a break, an overshoot, an offset.
          </p>
        </Container>
      </section>

      {routes.map(({ id, name, idea, Mark }) => (
        <section key={id} className="border-b border-border py-16 md:py-20" data-testid={`brand-route-${id}`}>
          <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="font-body text-sm font-medium tracking-widest text-accent">0{id}</span>
              <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
                {name}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{idea}</p>

              {/* Size / favicon tests */}
              <div className="mt-8">
                <span className="overline text-muted-foreground">Legibility · nav & favicon</span>
                <div className="mt-4 flex items-end gap-6 text-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Mark className="h-4 w-4" strokeWidth={2.4} />
                    <span className="text-[10px] text-muted-foreground">16</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Mark className="h-6 w-6" strokeWidth={2.2} />
                    <span className="text-[10px] text-muted-foreground">24</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Mark className="h-8 w-8" strokeWidth={2} />
                    <span className="text-[10px] text-muted-foreground">32</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:col-span-8 sm:grid-cols-2">
              <Swatch label="Mark · light">
                <Mark className="h-24 w-24" strokeWidth={1.8} />
              </Swatch>
              <Swatch label="Mark · dark" dark>
                <Mark className="h-24 w-24" strokeWidth={1.8} />
              </Swatch>
              <Swatch label="Horizontal lockup · light">
                <Mark className="h-9 w-9" strokeWidth={2} />
                <Wordmark className="text-3xl" />
              </Swatch>
              <Swatch label="Horizontal lockup · dark" dark>
                <Mark className="h-9 w-9" strokeWidth={2} />
                <Wordmark className="text-3xl" />
              </Swatch>
            </div>
          </Container>
        </section>
      ))}

      {/* Route 3b — wordmark-led */}
      <section className="border-b border-border py-16 md:py-20" data-testid="brand-route-wordmark">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="font-body text-sm font-medium tracking-widest text-accent">04</span>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
              Wordmark-led — the subtle route
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              No icon. The concept lives in the typography: a hairline rule beneath “Box” overshoots
              the word — the boundary exceeded, expressed editorially. Pairs with any mark above for
              favicon use.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:col-span-8 sm:grid-cols-2">
            <Swatch label="Wordmark · light">
              <Wordmark className="text-4xl" overshoot />
            </Swatch>
            <Swatch label="Wordmark · dark" dark>
              <Wordmark className="text-4xl" overshoot />
            </Swatch>
          </div>
        </Container>
      </section>
    </div>
  );
}
